// ============================================================
// 📁 کنار UsersPage.tsx   (فایل جدید)  —  اجزای ظاهری مشترک
// ============================================================
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { Check, X, ChevronLeft, ChevronRight, Search, AlertTriangle, Info, CheckCircle, XCircle, Eye, EyeOff } from "lucide-react";
import { PermMap, Perm, PermKey, pageLabels, permLabels, opLabels, emptyPerm, passwordScore } from "../../services/usersApi";

/* ---------------------------- زمینه تم ---------------------------- */
export const UmCtx = createContext<{ dark: boolean; T: any }>({ dark: true, T: {} });
export const useUm = () => useContext(UmCtx);

export const UM_CSS = `
.um{--gold:#d4af37;--gold2:#f5e6a8;--ac:#3b82f6;--ac2:#8b5cf6;--ok:#10b981;--warn:#f59e0b;--bad:#ef4444;position:relative;direction:rtl;font-family:inherit}
.um.dk{--card:rgba(255,255,255,.035);--card2:rgba(255,255,255,.06);--bd:rgba(255,255,255,.09);--inp:rgba(255,255,255,.05);--shadow:0 20px 60px rgba(0,0,0,.35);--row:rgba(255,255,255,.035);--pop:#141735;--modal:#0d0f2b}
.um.lt{--card:rgba(255,255,255,.92);--card2:#fff;--bd:rgba(15,23,42,.09);--inp:#f6f8fd;--shadow:0 18px 50px rgba(30,41,99,.12);--row:rgba(30,41,99,.04);--pop:#fff;--modal:#fff}
.um *{box-sizing:border-box}
.um-hero{position:relative;overflow:hidden;border-radius:26px;padding:30px 32px;margin-bottom:22px;border:1px solid var(--bd);background:linear-gradient(135deg,rgba(30,41,99,.55),rgba(76,29,149,.32) 55%,rgba(13,15,43,.55));box-shadow:var(--shadow)}
.um.lt .um-hero{background:linear-gradient(135deg,#eef2ff,#f5f0ff 55%,#fff8e6)}
.um-hero:before{content:"";position:absolute;inset:0;background-image:linear-gradient(rgba(255,255,255,.045) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,.045) 1px,transparent 1px);background-size:34px 34px;mask-image:radial-gradient(ellipse at 20% 0%,#000,transparent 70%);-webkit-mask-image:radial-gradient(ellipse at 20% 0%,#000,transparent 70%);pointer-events:none}
.um.lt .um-hero:before{background-image:linear-gradient(rgba(30,41,99,.06) 1px,transparent 1px),linear-gradient(90deg,rgba(30,41,99,.06) 1px,transparent 1px)}
.um-blob{position:absolute;border-radius:50%;filter:blur(70px);opacity:.55;pointer-events:none;animation:umFloat 14s ease-in-out infinite}
.um-blob.b1{width:340px;height:340px;background:rgba(59,130,246,.55);top:-140px;right:-60px}
.um-blob.b2{width:300px;height:300px;background:rgba(139,92,246,.5);bottom:-160px;left:8%;animation-delay:-5s}
.um-blob.b3{width:220px;height:220px;background:rgba(212,175,55,.32);top:-80px;left:38%;animation-delay:-9s}
.um.lt .um-blob{opacity:.28}
@keyframes umFloat{0%,100%{transform:translate(0,0) scale(1)}50%{transform:translate(30px,22px) scale(1.12)}}
.um-gold-line{position:absolute;top:0;left:10%;right:10%;height:2px;background:linear-gradient(90deg,transparent,var(--gold),transparent);opacity:.9}
.um-eyebrow{display:inline-flex;align-items:center;gap:8px;font-size:11px;font-weight:800;letter-spacing:1.6px;color:var(--gold);margin-bottom:8px}
.um-eyebrow:before{content:"";width:22px;height:2px;background:linear-gradient(90deg,var(--gold),transparent)}
.um-title{font-size:30px;font-weight:900;margin:0;letter-spacing:-.3px}
.um-card{position:relative;border-radius:20px;border:1px solid var(--bd);background:var(--card);backdrop-filter:blur(16px);-webkit-backdrop-filter:blur(16px);box-shadow:var(--shadow);padding:22px}
.um-card.gold:before{content:"";position:absolute;top:0;left:14%;right:14%;height:1.5px;background:linear-gradient(90deg,transparent,var(--gold),transparent);border-radius:2px}
.um-kpis{display:grid;grid-template-columns:repeat(2,1fr);gap:14px;margin-bottom:22px}@media (min-width:760px){.um-kpis{grid-template-columns:repeat(4,1fr)}}
.um-kpi{position:relative;border-radius:18px;padding:16px 18px;border:1px solid var(--bd);background:var(--card);overflow:hidden;transition:transform .25s,box-shadow .25s,border-color .25s;animation:umRise .55s both;animation-delay:calc(var(--i,0)*60ms)}
.um-kpi:hover{transform:translateY(-4px);box-shadow:0 18px 40px rgba(0,0,0,.28);border-color:var(--kc,var(--ac))}
.um-kpi:after{content:"";position:absolute;inset:auto -30px -40px auto;width:110px;height:110px;border-radius:50%;background:var(--kc,var(--ac));opacity:.12;filter:blur(18px)}
.um-kpi .ic{width:38px;height:38px;border-radius:12px;display:flex;align-items:center;justify-content:center;background:color-mix(in srgb,var(--kc,var(--ac)) 16%,transparent);border:1px solid color-mix(in srgb,var(--kc,var(--ac)) 30%,transparent);margin-bottom:12px}
.um-kpi .v{font-size:26px;font-weight:900;line-height:1.1;color:var(--kc,var(--ac))}
.um-kpi .l{font-size:11.5px;margin-top:4px;opacity:.75}
@keyframes umRise{from{opacity:0;transform:translateY(14px)}to{opacity:1;transform:none}}
.um-tabs{display:flex;gap:6px;overflow-x:auto;padding:6px;border-radius:18px;border:1px solid var(--bd);background:var(--card);margin-bottom:20px;scrollbar-width:none}
.um-tabs::-webkit-scrollbar{display:none}
.um-tab{position:relative;display:flex;align-items:center;gap:8px;padding:10px 16px;border-radius:13px;border:none;background:transparent;color:inherit;font-family:inherit;font-size:13px;font-weight:600;cursor:pointer;white-space:nowrap;opacity:.7;transition:all .25s}
.um-tab:hover{opacity:1;background:var(--card2)}
.um-tab.on{opacity:1;color:#fff;background:linear-gradient(135deg,var(--ac),var(--ac2));box-shadow:0 8px 24px rgba(59,130,246,.38)}
.um-tab.on:after{content:"";position:absolute;bottom:-3px;left:30%;right:30%;height:2px;background:var(--gold);border-radius:2px;box-shadow:0 0 10px var(--gold)}
.um-tab .bd{min-width:19px;height:19px;padding:0 5px;border-radius:10px;background:var(--bad);color:#fff;font-size:10.5px;font-weight:800;display:flex;align-items:center;justify-content:center}
.um-btn{position:relative;overflow:hidden;display:inline-flex;align-items:center;justify-content:center;gap:8px;padding:10px 18px;border-radius:12px;border:1px solid transparent;font-family:inherit;font-size:13px;font-weight:700;cursor:pointer;color:#fff;transition:transform .2s,box-shadow .2s,opacity .2s;white-space:nowrap}
.um-btn:disabled{opacity:.5;cursor:not-allowed;transform:none!important}
.um-btn:hover:not(:disabled){transform:translateY(-2px)}
.um-btn.sm{padding:7px 12px;font-size:12px;border-radius:10px}
.um-btn.primary{background:linear-gradient(135deg,var(--ac),var(--ac2));box-shadow:0 8px 24px rgba(59,130,246,.38)}
.um-btn.gold{background:linear-gradient(135deg,#b8902a,var(--gold) 55%,#f1d97a);color:#241a00;box-shadow:0 8px 24px rgba(212,175,55,.32)}
.um-btn.success{background:linear-gradient(135deg,#059669,#10b981);box-shadow:0 8px 22px rgba(16,185,129,.3)}
.um-btn.danger{background:linear-gradient(135deg,#dc2626,#ef4444);box-shadow:0 8px 22px rgba(239,68,68,.3)}
.um-btn.ghost{background:var(--card2);border-color:var(--bd);color:inherit}
.um-btn.primary:before,.um-btn.gold:before{content:"";position:absolute;top:0;left:-120%;width:60%;height:100%;background:linear-gradient(100deg,transparent,rgba(255,255,255,.35),transparent);transform:skewX(-20deg);transition:left .6s}
.um-btn.primary:hover:before,.um-btn.gold:hover:before{left:150%}
.um-icbtn{width:32px;height:32px;border-radius:10px;border:1px solid var(--bd);background:var(--card2);color:inherit;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;transition:all .2s}
.um-icbtn:hover{transform:translateY(-2px);border-color:var(--ac);color:var(--ac)}
.um-input,.um-select,.um-textarea{width:100%;padding:11px 14px;border-radius:12px;border:1.5px solid var(--bd);background:var(--inp);color:inherit;font-family:inherit;font-size:13px;outline:none;transition:border-color .2s,box-shadow .2s}
.um-input:focus,.um-select:focus,.um-textarea:focus{border-color:var(--ac);box-shadow:0 0 0 4px rgba(59,130,246,.16)}
.um-input::placeholder{opacity:.45}
.um-select option{background:var(--modal);color:inherit}
.um-label{display:block;font-size:11.5px;font-weight:700;margin-bottom:6px;opacity:.75}
.um-hint{font-size:11px;opacity:.55;margin-top:5px;line-height:1.7}
.um-grid{display:grid;gap:14px}
.um-chip{display:inline-flex;align-items:center;gap:6px;padding:4px 11px;border-radius:20px;font-size:11px;font-weight:700;border:1px solid transparent;white-space:nowrap}
.um-fchip{padding:8px 14px;border-radius:11px;border:1px solid var(--bd);background:var(--card2);color:inherit;font-family:inherit;font-size:12px;font-weight:600;cursor:pointer;transition:all .2s;opacity:.8}
.um-fchip.on{background:linear-gradient(135deg,var(--ac),var(--ac2));color:#fff;border-color:transparent;opacity:1}
.um-tbl{width:100%;border-collapse:separate;border-spacing:0 8px;font-size:13px}
.um-tbl th{padding:6px 14px;text-align:right;font-size:11.5px;font-weight:700;opacity:.55;white-space:nowrap}
.um-tbl td{padding:13px 14px;background:var(--row);border-top:1px solid transparent;border-bottom:1px solid transparent;transition:background .2s}
.um-tbl td:first-child{border-radius:0 14px 14px 0}.um-tbl td:last-child{border-radius:14px 0 0 14px}
.um-tbl tbody tr{animation:umRise .45s both;animation-delay:calc(var(--i,0)*28ms)}
.um-tbl tbody tr:hover td{background:var(--card2);border-color:var(--bd)}
.um-tbl tbody tr.sel td{background:rgba(59,130,246,.12)}
.um-ava{border-radius:13px;background:linear-gradient(135deg,var(--ac),var(--ac2));display:flex;align-items:center;justify-content:center;color:#fff;font-weight:800;overflow:hidden;flex-shrink:0;box-shadow:0 6px 16px rgba(59,130,246,.3)}
.um-ava img{width:100%;height:100%;object-fit:cover}
.um-overlay{position:fixed;inset:0;z-index:1000;background:rgba(3,5,18,.72);backdrop-filter:blur(10px);-webkit-backdrop-filter:blur(10px);display:flex;align-items:center;justify-content:center;padding:20px;animation:umFade .25s both}
.um-modal{position:relative;width:100%;max-height:92vh;display:flex;flex-direction:column;border-radius:24px;border:1px solid var(--bd);background:var(--modal);box-shadow:0 40px 120px rgba(0,0,0,.6);animation:umPop .35s cubic-bezier(.2,1.2,.3,1) both;overflow:hidden}
.um-modal:before{content:"";position:absolute;top:0;left:12%;right:12%;height:2px;background:linear-gradient(90deg,transparent,var(--gold),transparent);z-index:2}
.um-mh{padding:24px 28px 16px;display:flex;align-items:flex-start;justify-content:space-between;gap:14px}
.um-mb{padding:4px 28px 22px;overflow-y:auto;flex:1}
.um-mf{padding:16px 28px;border-top:1px solid var(--bd);display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap;background:var(--card)}
@keyframes umPop{from{opacity:0;transform:translateY(24px) scale(.94)}to{opacity:1;transform:none}}
@keyframes umFade{from{opacity:0}to{opacity:1}}
.um-pm{width:100%;border-collapse:collapse;font-size:12.5px}
.um-pm th{padding:10px 8px;font-size:11px;font-weight:700;opacity:.7;text-align:center;white-space:nowrap}
.um-pm th:first-child,.um-pm td:first-child{text-align:right;padding-right:14px}
.um-pm td{padding:9px 8px;text-align:center;border-top:1px solid var(--bd)}
.um-pm tr:hover td{background:var(--row)}
.um-pm th.ap{color:var(--gold);opacity:1}
.um-cb{width:22px;height:22px;border-radius:7px;border:2px solid var(--bd);background:transparent;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;transition:all .2s;padding:0;color:#fff}
.um-cb:hover:not(:disabled){border-color:var(--ac)}
.um-cb.on{background:linear-gradient(135deg,var(--ac),var(--ac2));border-color:transparent;box-shadow:0 4px 12px rgba(59,130,246,.4)}
.um-cb.on.ap{background:linear-gradient(135deg,#b8902a,var(--gold));box-shadow:0 4px 12px rgba(212,175,55,.45);color:#241a00}
.um-cb:disabled{opacity:.45;cursor:not-allowed}
.um-tg{position:relative;width:42px;height:24px;border-radius:14px;border:none;background:var(--bd);cursor:pointer;transition:background .25s;padding:0;flex-shrink:0}
.um-tg:after{content:"";position:absolute;top:3px;right:3px;width:18px;height:18px;border-radius:50%;background:#fff;transition:transform .25s;box-shadow:0 2px 6px rgba(0,0,0,.3)}
.um-tg.on{background:linear-gradient(135deg,var(--ac),var(--ac2))}.um-tg.on:after{transform:translateX(-18px)}
.um-tg:disabled{opacity:.5;cursor:not-allowed}
.um-pop{position:fixed;z-index:1200;min-width:210px;padding:7px;border-radius:15px;border:1px solid var(--bd);background:var(--pop);box-shadow:0 24px 60px rgba(0,0,0,.5);animation:umPop .2s both}
.um-pop button{width:100%;display:flex;align-items:center;gap:10px;padding:9px 12px;border:none;background:transparent;color:inherit;font-family:inherit;font-size:12.5px;border-radius:9px;cursor:pointer;text-align:right}
.um-pop button:hover{background:var(--row)}.um-pop button.dg{color:var(--bad)}.um-pop hr{border:none;border-top:1px solid var(--bd);margin:5px 0}
.um-toast{position:fixed;top:22px;left:50%;transform:translateX(-50%);z-index:3000;display:flex;flex-direction:column;gap:8px;align-items:center;pointer-events:none}
.um-toast>div{pointer-events:auto;padding:12px 20px;border-radius:14px;border:1px solid var(--bd);background:var(--pop);box-shadow:0 18px 50px rgba(0,0,0,.45);font-size:13px;font-weight:600;display:flex;align-items:center;gap:10px;animation:umPop .3s both;max-width:92vw}
.um-bar{height:6px;border-radius:6px;background:var(--bd);overflow:hidden;margin-top:8px}.um-bar i{display:block;height:100%;border-radius:6px;transition:width .3s,background .3s}
.um-sec{font-size:13px;font-weight:800;margin:22px 0 12px;display:flex;align-items:center;gap:9px}
.um-sec:before{content:"";width:4px;height:16px;border-radius:2px;background:linear-gradient(var(--gold),var(--ac))}
.um-empty{text-align:center;padding:54px 20px;opacity:.7}
.um-spin{width:34px;height:34px;border:3px solid rgba(59,130,246,.2);border-top-color:var(--ac);border-radius:50%;animation:umSpin .8s linear infinite;margin:0 auto 12px}
@keyframes umSpin{to{transform:rotate(360deg)}}
.um-code{font-family:ui-monospace,Consolas,monospace;direction:ltr;letter-spacing:2px}
.um-flag{display:inline-flex;align-items:center;gap:5px;padding:3px 9px;border-radius:8px;font-size:10.5px;font-weight:700;background:rgba(245,158,11,.14);color:var(--warn);border:1px solid rgba(245,158,11,.28);margin:2px}
.um-scroll{overflow-x:auto}
.um-kv{display:flex;justify-content:space-between;gap:12px;padding:9px 0;border-bottom:1px dashed var(--bd);font-size:12.5px}
.um-kv:last-child{border-bottom:none}
@media (max-width:760px){.um-hero{padding:22px 18px}.um-title{font-size:23px}.um-mh,.um-mb,.um-mf{padding-left:16px;padding-right:16px}}
@media print{.um-hero,.um-tabs,.um-noprint{display:none!important}.um-overlay{position:static;background:none;backdrop-filter:none}}
@media (prefers-reduced-motion:reduce){.um *{animation:none!important;transition:none!important}}
`;

export function UmStyle() { return <style>{UM_CSS}</style>; }

/* ---------------------------- اعلان‌ها ---------------------------- */
type ToastItem = { id: number; msg: string; kind: "ok" | "err" | "info" };
let toasts: ToastItem[] = [];
const toastSubs = new Set<() => void>();
export function toast(msg: string, kind: "ok" | "err" | "info" = "ok") {
  const id = Date.now() + Math.random();
  toasts = [...toasts, { id, msg, kind }];
  toastSubs.forEach((f) => f());
  setTimeout(() => { toasts = toasts.filter((t) => t.id !== id); toastSubs.forEach((f) => f()); }, kind === "err" ? 6000 : 3500);
}
export function ToastHost() {
  const [, force] = useState(0);
  useEffect(() => { const f = () => force((n) => n + 1); toastSubs.add(f); return () => { toastSubs.delete(f); }; }, []);
  return (
    <div className="um-toast">
      {toasts.map((t) => (
        <div key={t.id} style={{ borderColor: t.kind === "ok" ? "rgba(16,185,129,.5)" : t.kind === "err" ? "rgba(239,68,68,.5)" : undefined }}>
          {t.kind === "ok" ? <CheckCircle size={17} color="#10b981" /> : t.kind === "err" ? <XCircle size={17} color="#ef4444" /> : <Info size={17} color="#3b82f6" />}
          {t.msg}
        </div>
      ))}
    </div>
  );
}

/* ---------------------- دیالوگ‌های تایید/پسورد/توضیح ---------------------- */
type DlgReq = { kind: "confirm" | "password" | "prompt"; title: string; text?: string; danger?: boolean; okLabel?: string; placeholder?: string; required?: boolean; resolve: (v: any) => void };
let dlgCur: DlgReq | null = null;
const dlgSubs = new Set<() => void>();
const openDlg = (r: Omit<DlgReq, "resolve">) => new Promise<any>((resolve) => { dlgCur = { ...r, resolve }; dlgSubs.forEach((f) => f()); });
export const dlg = {
  confirm: (title: string, text?: string, danger = false, okLabel = "تایید"): Promise<boolean> => openDlg({ kind: "confirm", title, text, danger, okLabel }),
  /** برمی‌گرداند پسورد یا null */
  password: (title: string, text?: string): Promise<string | null> => openDlg({ kind: "password", title, text, okLabel: "تایید و ادامه" }),
  prompt: (title: string, text?: string, placeholder = "", required = false): Promise<string | null> => openDlg({ kind: "prompt", title, text, placeholder, required, okLabel: "ثبت" }),
};
export function DialogHost() {
  const [, force] = useState(0);
  const [val, setVal] = useState("");
  const [show, setShow] = useState(false);
  useEffect(() => { const f = () => { setVal(""); setShow(false); force((n) => n + 1); }; dlgSubs.add(f); return () => { dlgSubs.delete(f); }; }, []);
  const d = dlgCur;
  if (!d) return null;
  const close = (v: any) => { const r = d.resolve; dlgCur = null; dlgSubs.forEach((f) => f()); r(v); };
  const needVal = d.kind !== "confirm";
  const disabled = needVal && (d.kind === "password" || d.required) && !val.trim();
  return (
    <div className="um-overlay" style={{ zIndex: 2000 }} onMouseDown={() => close(d.kind === "confirm" ? false : null)}>
      <div className="um-modal" style={{ maxWidth: 440 }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="um-mh">
          <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
            <div style={{ width: 42, height: 42, borderRadius: 13, display: "flex", alignItems: "center", justifyContent: "center", background: d.danger ? "rgba(239,68,68,.15)" : d.kind === "password" ? "rgba(212,175,55,.16)" : "rgba(59,130,246,.15)" }}>
              <AlertTriangle size={20} color={d.danger ? "#ef4444" : d.kind === "password" ? "#d4af37" : "#3b82f6"} />
            </div>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>{d.title}</div>
              {d.text && <div style={{ fontSize: 12.5, opacity: 0.7, marginTop: 4, lineHeight: 1.8 }}>{d.text}</div>}
            </div>
          </div>
        </div>
        {needVal && (
          <div className="um-mb" style={{ paddingTop: 0 }}>
            <div style={{ position: "relative" }}>
              <input
                autoFocus
                className="um-input"
                type={d.kind === "password" && !show ? "password" : "text"}
                value={val}
                placeholder={d.kind === "password" ? "پسورد خودتان" : d.placeholder}
                onChange={(e) => setVal(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !disabled && close(val)}
                autoComplete="current-password"
              />
              {d.kind === "password" && (
                <button type="button" onClick={() => setShow(!show)} style={{ position: "absolute", left: 10, top: 10, background: "none", border: "none", color: "inherit", opacity: 0.6, cursor: "pointer" }}>
                  {show ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              )}
            </div>
          </div>
        )}
        <div className="um-mf">
          <button className="um-btn ghost" onClick={() => close(d.kind === "confirm" ? false : null)}>انصراف</button>
          <button className={`um-btn ${d.danger ? "danger" : "primary"}`} disabled={disabled} onClick={() => close(d.kind === "confirm" ? true : val)}>{d.okLabel}</button>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- اجزا ----------------------------- */
export function Modal({ title, subtitle, onClose, children, footer, width = 640, icon }: { title: string; subtitle?: string; onClose: () => void; children: React.ReactNode; footer?: React.ReactNode; width?: number; icon?: React.ReactNode }) {
  useEffect(() => {
    const k = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", k);
    return () => window.removeEventListener("keydown", k);
  }, [onClose]);
  return (
    <div className="um-overlay" onMouseDown={onClose}>
      <div className="um-modal" style={{ maxWidth: width }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="um-mh">
          <div style={{ display: "flex", gap: 14, alignItems: "center", minWidth: 0 }}>
            {icon && <div style={{ width: 46, height: 46, borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg,rgba(59,130,246,.2),rgba(139,92,246,.2))", border: "1px solid var(--bd)", flexShrink: 0 }}>{icon}</div>}
            <div style={{ minWidth: 0 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>{title}</h2>
              {subtitle && <p style={{ margin: "4px 0 0", fontSize: 12, opacity: 0.6 }}>{subtitle}</p>}
            </div>
          </div>
          <button className="um-icbtn" onClick={onClose} aria-label="بستن"><X size={16} /></button>
        </div>
        <div className="um-mb">{children}</div>
        {footer && <div className="um-mf">{footer}</div>}
      </div>
    </div>
  );
}

export function Btn({ kind = "primary", size, icon, loading, children, ...rest }: { kind?: "primary" | "ghost" | "danger" | "gold" | "success"; size?: "sm"; icon?: React.ReactNode; loading?: boolean } & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button {...rest} disabled={rest.disabled || loading} className={`um-btn ${kind} ${size || ""} ${rest.className || ""}`}>
      {loading ? <span className="um-spin" style={{ width: 15, height: 15, borderWidth: 2, margin: 0, borderTopColor: "#fff" }} /> : icon}
      {children}
    </button>
  );
}

export function Badge({ color, children, dot }: { color: string; children: React.ReactNode; dot?: boolean }) {
  return (
    <span className="um-chip" style={{ background: `${color}1f`, color, borderColor: `${color}40` }}>
      {dot && <i style={{ width: 6, height: 6, borderRadius: "50%", background: color, boxShadow: `0 0 8px ${color}` }} />}
      {children}
    </span>
  );
}
export const stateColor = (s: string) => (s === "active" ? "#10b981" : s === "invited" ? "#3b82f6" : s === "suspended" ? "#f59e0b" : s === "invite_expired" ? "#f97316" : s === "deleted" ? "#64748b" : "#ef4444");
export const roleColor = (r: string) => (r === "admin" ? "#f59e0b" : r === "manager" ? "#8b5cf6" : "#10b981");

export function Toggle({ on, onChange, disabled }: { on: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return <button type="button" role="switch" aria-checked={on} className={`um-tg ${on ? "on" : ""}`} disabled={disabled} onClick={() => onChange(!on)} />;
}
export function ToggleRow({ label, hint, on, onChange, disabled }: { label: string; hint?: string; on: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14, padding: "11px 0", borderBottom: "1px dashed var(--bd)" }}>
      <div><div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>{hint && <div className="um-hint" style={{ marginTop: 2 }}>{hint}</div>}</div>
      <Toggle on={on} onChange={onChange} disabled={disabled} />
    </div>
  );
}
export function Field({ label, hint, children, span }: { label: string; hint?: string; children: React.ReactNode; span?: number }) {
  return (
    <div style={span ? { gridColumn: `span ${span}` } : undefined}>
      <label className="um-label">{label}</label>
      {children}
      {hint && <div className="um-hint">{hint}</div>}
    </div>
  );
}
export function Avatar({ name, src, size = 40 }: { name: string; src?: string | null; size?: number }) {
  const [bad, setBad] = useState(false);
  useEffect(() => { setBad(false); }, [src]);
  return <div className="um-ava" style={{ width: size, height: size, fontSize: size * 0.4 }}>{src && !bad ? <img src={src} alt="" onError={() => setBad(true)} /> : (name || "?").trim().charAt(0)}</div>;
}
export function Spinner({ text = "در حال بارگذاری..." }: { text?: string }) {
  return <div className="um-empty"><div className="um-spin" />{text}</div>;
}
export function Empty({ icon, title, sub }: { icon: React.ReactNode; title: string; sub?: string }) {
  return <div className="um-empty"><div style={{ opacity: 0.4, marginBottom: 12 }}>{icon}</div><div style={{ fontSize: 14, fontWeight: 700 }}>{title}</div>{sub && <div style={{ fontSize: 12, marginTop: 6, opacity: 0.7 }}>{sub}</div>}</div>;
}
export function SectionTitle({ children }: { children: React.ReactNode }) { return <div className="um-sec">{children}</div>; }

export function Tabs({ tabs, active, onChange }: { tabs: { key: string; label: string; icon: React.ReactNode; badge?: number }[]; active: string; onChange: (k: string) => void }) {
  return (
    <div className="um-tabs" role="tablist">
      {tabs.map((t) => (
        <button key={t.key} role="tab" aria-selected={active === t.key} className={`um-tab ${active === t.key ? "on" : ""}`} onClick={() => onChange(t.key)}>
          {t.icon}{t.label}{!!t.badge && <span className="bd">{t.badge}</span>}
        </button>
      ))}
    </div>
  );
}

export function CountUp({ value }: { value: number }) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let raf = 0; const t0 = performance.now(); const dur = 700;
    const step = (t: number) => { const p = Math.min(1, (t - t0) / dur); setV(Math.round(value * (1 - Math.pow(1 - p, 3)))); if (p < 1) raf = requestAnimationFrame(step); };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <>{v}</>;
}
export function Kpi({ label, value, color, icon, i = 0 }: { label: string; value: number; color: string; icon: React.ReactNode; i?: number }) {
  return (
    <div className="um-kpi" style={{ ["--kc" as any]: color, ["--i" as any]: i }}>
      <div className="ic">{icon}</div>
      <div className="v"><CountUp value={value} /></div>
      <div className="l">{label}</div>
    </div>
  );
}
export function Sparkline({ data, color = "#3b82f6", height = 46, width = 220 }: { data: number[]; color?: string; height?: number; width?: number }) {
  if (!data.length) return null;
  const max = Math.max(1, ...data);
  const step = data.length > 1 ? width / (data.length - 1) : width;
  const pts = data.map((d, i) => [i * step, height - 4 - (d / max) * (height - 10)]);
  const line = pts.map((p, i) => `${i ? "L" : "M"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
  const id = "sg" + color.replace("#", "");
  return (
    <svg width="100%" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ display: "block" }}>
      <defs><linearGradient id={id} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={color} stopOpacity=".4" /><stop offset="1" stopColor={color} stopOpacity="0" /></linearGradient></defs>
      <path d={`${line} L${width},${height} L0,${height} Z`} fill={`url(#${id})`} />
      <path d={line} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {pts.length > 0 && <circle cx={pts[pts.length - 1][0]} cy={pts[pts.length - 1][1]} r="3.5" fill={color} />}
    </svg>
  );
}

export function PasswordMeter({ value, min }: { value: string; min: number }) {
  const n = passwordScore(value, min);
  const cols = ["#ef4444", "#ef4444", "#f59e0b", "#eab308", "#22c55e", "#10b981"];
  const txt = ["", "ضعیف", "ضعیف", "متوسط", "خوب", "عالی"];
  if (!value) return <div className="um-hint">حداقل {min} کاراکتر؛ ترکیب حروف بزرگ، کوچک، عدد و علامت</div>;
  return (<><div className="um-bar"><i style={{ width: `${n * 20}%`, background: cols[n] }} /></div><div className="um-hint" style={{ color: cols[n], opacity: 1 }}>قدرت پسورد: {txt[n]}</div></>);
}

export function Pager({ total, page, per, onPage, onPer }: { total: number; page: number; per: number; onPage: (p: number) => void; onPer?: (n: number) => void }) {
  const pages = Math.max(1, Math.ceil(total / per));
  return (
    <div className="um-noprint" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginTop: 14, fontSize: 12 }}>
      <span style={{ opacity: 0.65 }}>{total} مورد — صفحه {page} از {pages}</span>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        {onPer && (
          <select className="um-select" style={{ width: 84, padding: "7px 10px" }} value={per} onChange={(e) => { onPer(+e.target.value); onPage(1); }}>
            {[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        )}
        <button className="um-icbtn" disabled={page <= 1} onClick={() => onPage(page - 1)} aria-label="قبلی"><ChevronRight size={15} /></button>
        <button className="um-icbtn" disabled={page >= pages} onClick={() => onPage(page + 1)} aria-label="بعدی"><ChevronLeft size={15} /></button>
      </div>
    </div>
  );
}
export function SearchBox({ value, onChange, placeholder = "جستجو..." }: { value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
      <Search size={15} style={{ position: "absolute", right: 13, top: 12, opacity: 0.5 }} />
      <input className="um-input" style={{ paddingRight: 38 }} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
    </div>
  );
}

/* منوی شناور (بیرون از overflow جدول) */
export function ActionMenu({ trigger, items }: { trigger: React.ReactNode; items: ({ label: string; icon?: React.ReactNode; onClick: () => void; danger?: boolean; hidden?: boolean } | "-")[] }) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!pos) return;
    const close = () => setPos(null);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => { window.removeEventListener("scroll", close, true); window.removeEventListener("resize", close); };
  }, [pos]);
  const vis = items.filter((i) => i === "-" || !i.hidden);
  return (
    <>
      <button ref={btn} className="um-icbtn" onClick={() => {
        const r = btn.current!.getBoundingClientRect();
        const h = vis.length * 38 + 20;
        setPos({ x: Math.max(8, r.left), y: r.bottom + h > window.innerHeight ? Math.max(8, r.top - h - 6) : r.bottom + 6 });
      }}>{trigger}</button>
      {pos && (
        <>
          <div style={{ position: "fixed", inset: 0, zIndex: 1199 }} onMouseDown={() => setPos(null)} />
          <div className="um-pop" style={{ left: pos.x, top: pos.y }}>
            {vis.map((it, i) => it === "-" ? <hr key={i} /> : (
              <button key={i} className={it.danger ? "dg" : ""} onClick={() => { setPos(null); it.onClick(); }}>{it.icon}{it.label}</button>
            ))}
          </div>
        </>
      )}
    </>
  );
}

/* ماتریس دسترسی با ستون «تایید» */
export function PermMatrix({ value, onChange, disabled, pages }: { value: PermMap; onChange: (v: PermMap) => void; disabled?: boolean; pages?: string[] }) {
  const keys: PermKey[] = ["view", "add", "edit", "delete", "approve"];
  const list = pages || Object.keys(pageLabels);
  const get = (p: string): Perm => value[p] || emptyPerm();
  const setCell = (p: string, k: PermKey, v: boolean) => {
    const cur = { ...get(p), [k]: v };
    if (k !== "view" && v) cur.view = true;
    if (k === "view" && !v) { cur.add = false; cur.edit = false; cur.delete = false; cur.approve = false; }
    onChange({ ...value, [p]: cur });
  };
  const setCol = (k: PermKey, v: boolean) => { const n: PermMap = { ...value }; list.forEach((p) => { const cur = { ...get(p), [k]: v }; if (k !== "view" && v) cur.view = true; if (k === "view" && !v) { cur.add = cur.edit = cur.delete = cur.approve = false; } n[p] = cur; }); onChange(n); };
  const setRow = (p: string, v: boolean) => onChange({ ...value, [p]: { view: v, add: v, edit: v, delete: v, approve: false } });
  return (
    <div style={{ borderRadius: 16, border: "1px solid var(--bd)", overflow: "hidden" }}>
      <div className="um-scroll">
        <table className="um-pm">
          <thead>
            <tr style={{ background: "var(--card)" }}>
              <th>صفحه</th>
              {keys.map((k) => (
                <th key={k} className={k === "approve" ? "ap" : ""}>
                  <button type="button" disabled={disabled} onClick={() => setCol(k, !list.every((p) => get(p)[k]))} style={{ background: "none", border: "none", color: "inherit", font: "inherit", cursor: disabled ? "default" : "pointer" }} title="انتخاب همه ستون">
                    {permLabels[k]}{k === "approve" ? " ✦" : ""}
                  </button>
                </th>
              ))}
              <th>همه</th>
            </tr>
          </thead>
          <tbody>
            {list.map((p) => (
              <tr key={p}>
                <td style={{ fontWeight: 600 }}>{pageLabels[p] || p}</td>
                {keys.map((k) => (
                  <td key={k}>
                    <button type="button" disabled={disabled} className={`um-cb ${get(p)[k] ? "on" : ""} ${k === "approve" ? "ap" : ""}`} onClick={() => setCell(p, k, !get(p)[k])} aria-label={`${pageLabels[p]} ${permLabels[k]}`}>
                      {get(p)[k] && <Check size={13} strokeWidth={3.5} />}
                    </button>
                  </td>
                ))}
                <td>
                  <button type="button" disabled={disabled} className="um-icbtn" style={{ width: 26, height: 26 }} onClick={() => setRow(p, !(get(p).view && get(p).add && get(p).edit && get(p).delete))} title="انتخاب همه سطر (بدون تایید)"><Check size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ padding: "10px 16px", fontSize: 11, opacity: 0.6, background: "var(--card)", borderTop: "1px solid var(--bd)" }}>
        ✦ «تایید» یعنی کاربر می‌تواند عملیاتی را که شرط تایید دارد در آن بخش تایید یا رد کند. دسترسی «تایید» همیشه شامل «مشاهده» است.
      </div>
    </div>
  );
}

export function OpsGrid({ value, onChange, disabled }: { value: Record<string, boolean>; onChange: (v: Record<string, boolean>) => void; disabled?: boolean }) {
  return (
    <div className="um-grid" style={{ gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))" }}>
      {Object.keys(opLabels).map((k) => (
        <div key={k} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "11px 14px", borderRadius: 13, border: "1px solid var(--bd)", background: "var(--card)" }}>
          <span style={{ fontSize: 12.5, fontWeight: 600 }}>{opLabels[k]}</span>
          <Toggle on={value[k] !== false} onChange={(v) => onChange({ ...value, [k]: v })} disabled={disabled} />
        </div>
      ))}
    </div>
  );
}

/* ------------- فراخوانی با تایید پسورد (برای عملیات حساس) ------------- */
export async function stepCall(fn: (pw?: string) => Promise<any>, title = "تایید هویت", text = "این عملیات حساس است؛ برای ادامه پسورد خودتان را وارد کنید."): Promise<any> {
  let pw: string | undefined;
  for (let i = 0; i < 3; i++) {
    const r = await fn(pw);
    if (!r || !r.needPassword) return r;
    const p = await dlg.password(title, pw ? r.message : text);
    if (p === null) return { success: false, message: "لغو شد", cancelled: true };
    pw = p;
  }
  return { success: false, message: "تایید هویت ناموفق بود" };
}
/** اعلان نتیجه؛ true اگر موفق بود */
export function notify(r: any, okMsg?: string): boolean {
  if (r && r.success) { toast(okMsg || r.message || "انجام شد", "ok"); return true; }
  if (r && r.cancelled) return false;
  toast((r && r.message) || "خطا در انجام عملیات", "err");
  return false;
}
export const arr = (r: any, ...keys: string[]): any[] => { for (const k of keys) if (r && Array.isArray(r[k])) return r[k]; return []; };
